import {
  INTERCOM_HISTORY_IMPORT_DEFAULT_FLOOR,
  type IntercomHistoryImport,
} from '@sbc/domain-models/gateway';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useIntercomHistoryImport,
  usePauseIntercomHistoryImport,
  useResumeIntercomHistoryImport,
  useStartIntercomHistoryImport,
} from '@/lib/api/intercom-history-import';

const STATUS_LABELS: Record<IntercomHistoryImport['status'], string> = {
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
};

export function importStage(run: IntercomHistoryImport): string {
  if (run.status === 'completed') {
    return `Finished ${formatTime(run.finishedAt)}`;
  }
  if (run.phase === 'open') {
    return 'Open and snoozed conversations';
  }
  if (run.phase === 'history') {
    return `History, day ${run.windowDay ?? '…'} (back to ${run.floorDate})`;
  }
  return 'Retrying conversations that failed';
}

function formatTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

export function ImportRunSummary({ run }: { run: IntercomHistoryImport }) {
  const expected =
    run.openTotal === null && run.historyTotal === null
      ? null
      : (run.openTotal ?? 0) + (run.historyTotal ?? 0);
  const rows: [string, string][] = [
    ['Stage', importStage(run)],
    ['Looked at', expected === null ? String(run.scanned) : `${run.scanned} of ~${expected}`],
    ['Imported', String(run.imported)],
    ['Unchanged since last import', String(run.unchanged)],
    ['Skipped (no member message)', String(run.noMemberMessage)],
    ['Failed', String(run.failed)],
    ['Started', formatTime(run.startedAt)],
    ['Last progress', formatTime(run.updatedAt)],
  ];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={run.status === 'running' ? 'default' : 'outline'}>
          {STATUS_LABELS[run.status]}
        </Badge>
        {run.force && <Badge variant="secondary">Re-reading everything</Badge>}
      </div>
      <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[14rem_1fr]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-[var(--color-muted-foreground)]">{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {run.status !== 'completed' && run.resumeAfter && new Date(run.resumeAfter) > new Date() && (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Giving way to Intercom’s rate limit until {formatTime(run.resumeAfter)}.
        </p>
      )}
      {run.lastError && (
        <p className="text-sm break-words text-[var(--color-destructive)]">
          Last error: {run.lastError}
        </p>
      )}
    </div>
  );
}

export function IntercomHistoryImportPanel() {
  const importQuery = useIntercomHistoryImport();
  const start = useStartIntercomHistoryImport();
  const pause = usePauseIntercomHistoryImport();
  const resume = useResumeIntercomHistoryImport();
  const [floorDate, setFloorDate] = useState(INTERCOM_HISTORY_IMPORT_DEFAULT_FLOOR);
  const [force, setForce] = useState(false);

  const run = importQuery.data?.run ?? null;
  const busy = start.isPending || pause.isPending || resume.isPending;
  const actionError = start.error ?? pause.error ?? resume.error;
  const active = run !== null && run.status !== 'completed';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">History import</CardTitle>
        <CardDescription>
          Brings every Intercom conversation into the inbox, open ones first, then one day at a time
          back to the start date. Nothing is drafted or sent. Safe to run again: a second run only
          reads what changed in Intercom.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {importQuery.isError && (
          <p className="text-sm text-[var(--color-destructive)]">
            Failed to load the import: {importQuery.error.message}
          </p>
        )}
        {run ? (
          <ImportRunSummary run={run} />
        ) : (
          importQuery.isSuccess && (
            <p className="text-sm text-[var(--color-muted-foreground)]">No import has run yet.</p>
          )
        )}
        {active ? (
          <div className="flex gap-2">
            {run.status === 'running' ? (
              <Button variant="outline" disabled={busy} onClick={() => pause.mutate(undefined)}>
                Pause
              </Button>
            ) : (
              <Button disabled={busy} onClick={() => resume.mutate(undefined)}>
                Resume
              </Button>
            )}
          </div>
        ) : (
          importQuery.isSuccess && (
            <div className="flex flex-wrap items-end gap-4 rounded-md border p-4">
              <div className="space-y-1">
                <Label htmlFor="intercom-import-floor">Import back to</Label>
                <Input
                  id="intercom-import-floor"
                  type="date"
                  className="w-44"
                  value={floorDate}
                  onChange={(event) => setFloorDate(event.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-4"
                  checked={force}
                  onChange={(event) => setForce(event.target.checked)}
                />
                Re-read conversations already imported
              </label>
              <Button
                disabled={busy || floorDate === ''}
                onClick={() => start.mutate({ floorDate, force })}
              >
                {run ? 'Start another import' : 'Start import'}
              </Button>
            </div>
          )
        )}
        {actionError && (
          <p className="text-sm text-[var(--color-destructive)]">{actionError.message}</p>
        )}
      </CardContent>
    </Card>
  );
}
